import BadgeGroup from '3-components/BadgeGroup/BadgeGroup';
import Chip, { type BadgeProps } from '3-components/Chip/Chip';
import Image, { type ImageProps } from '2-core-elements/Image/Image';
import Icon, { type IconString } from '2-core-elements/Icon/Icon';
import { type FC } from 'react';

interface CardInfoItemProps {
    icon: IconString;
    children: React.ReactNode;
}

const InfoItem: FC<CardInfoItemProps> = ({
    icon,
    children,
}) => {
    return (
        <div className="card-info-item">
            <Icon icon={icon} />
            {children}
        </div>
    );
};

export interface CardAuthorProps {
    img?: Pick<ImageProps, 'imgSrc' | 'imgAlt' | 'imgHeight' | 'imgWidth'| 'imgSrcSet'>;
    title: string;
    subtitle?: string;
}

const CardAuthor: FC<CardAuthorProps> = ({
    img,
    title,
    subtitle,
}) => {
    return (
        <div className="card-author">
            {img && (
                <div className="author-img">
                    <Image aspectRatio="1/1" imgSrc={img.imgSrc} imgAlt={img.imgAlt} imgSrcSet={img.imgSrcSet} />
                </div>
            )}
            <div className="author-info">
                <address className="author-title">{title}</address>
                {subtitle && (
                    <p className="author-subtitle">{subtitle}</p>
                )}
            </div>
        </div>
    );
};

export type ArticleCardProps = {
    img?: Pick<ImageProps, 'imgSrc' | 'imgAlt' | 'imgHeight' | 'imgWidth'| 'imgSrcSet'>;
    link?: { href: string; label?: string; target?: '_self' | '_blank' };
    title: string;
    tags?: BadgeProps[];
    date?: string;
    minRead?: string;
    children?: React.ReactNode;
    authors?: CardAuthorProps[];
    size?: 'sm' | 'md' | 'lg' | null; // null for custom size on breakpoint
    padded?: boolean;
};

const CardArticle = ({
    img,
    link = { href: '', label: '', target: '_self' },
    title,
    children,
    tags = [],
    date,
    minRead,
    authors,
    size,
    padded = true,
}: ArticleCardProps): JSX.Element => {
    return (
        <a href={link.href} className={`article-card ${size ? `card-${size}` : ''}${!padded ? ` no-padded` : ''}`} aria-label={title}>
            {img && (
                <div className="card-img">
                    <Image aspectRatio="16/9" imgSrc={img.imgSrc} imgAlt={img.imgAlt} imgSrcSet={img.imgSrcSet} />
                </div>
            )}
            {!!tags?.length && (
                <BadgeGroup
                    className={`card-tags`}
                >
                    {tags.map((tag, i) => {
                        const key =`${tag.label}-${i}`;
                        return <Chip key={key} label={tag.label} skin={tag.skin} size="lg" />;
                    })}
                </BadgeGroup>
            )}
            <div className="card-content">
                {(date || minRead) && (
                    <div className="card-info">
                        {date && (
                            <InfoItem icon="calendar">
                                <time dateTime={date}>
                                    {date}
                                </time>
                            </InfoItem>
                        )}
                        {minRead && (
                            <InfoItem icon="time">
                                <span>{minRead}</span>
                            </InfoItem>
                        )}
                    </div>
                )}
                <h2 className="card-title">
                    {title}
                </h2>
                {children && (
                    <div className="card-description">
                        {children}
                    </div>
                )}
                {!!authors?.length && (
                    <div className="card-authors">
                        {authors.map((props, i) => {
                            const key = `${props.title}-${i}`;

                            return (
                                <CardAuthor
                                    key={key}
                                    // eslint-disable-next-line react/jsx-props-no-spreading
                                    {...props}
                                />
                            );
                        })}
                    </div>
                )}
            </div>
        </a>
    );
};

export default CardArticle;
